import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

import { createMqStyles } from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import usePlaceIdDetails from '@fhs-legacy/frontend/src/hooks/geolocation/use-place-id-details';

import { AddressSearchInput, LocationPrediction } from '../search-field';

import { DeliveryAddressFieldHandle, DeliveryAddressFieldProps } from './types';

const PLACEHOLDER = 'Enter delivery address';
const HELPER_TEXT = 'Bldg/Suite/Apt info and delivery instructions added at checkout.';
const ADDRESS_ERROR = 'Oops! Address not recognized.';

const getPredictionTestID = (index: number) => `store-locator-delivery-address-suggestion-${index}`;

/**
 * Delivery address typeahead backed by Google Places address predictions.
 *
 * Unlike the pickup search it never touches the shared geolocation state, and it only reports a
 * selection once the picked prediction has resolved to a street address a courier can deliver to.
 */
export const DeliveryAddressField = forwardRef<
  DeliveryAddressFieldHandle,
  DeliveryAddressFieldProps
>(function DeliveryAddressField({ value, onChange, disabled = false }, forwardedRef) {
  const styles = useDeliveryAddressFieldMqStyles();
  const getPlaceDetails = usePlaceIdDetails();
  const [hasAddressError, setHasAddressError] = useState(false);

  // Bumped by every pick, edit and clear, so a details lookup that settles after the guest has
  // moved on is dropped instead of reporting an address the field no longer shows.
  const lookupIdRef = useRef(0);
  // Lookups settle after the render that started them, by which time the parent may have
  // handed down a new callback.
  const onChangeRef = useBoxRef(onChange);
  const hasSelection = Boolean(value);

  // A selection the parent supplies replaces whatever the field shows, so a lookup still in
  // flight, or the error from one that failed, belongs to an address that is gone. Keyed on the
  // text rather than the object, so a re-created but identical selection changes nothing.
  const valueDescription = value?.description;
  useEffect(() => {
    if (!valueDescription) {
      return;
    }

    lookupIdRef.current += 1;
    setHasAddressError(false);
  }, [valueDescription]);

  const showAddressError = useCallback(() => {
    setHasAddressError(true);
    // Web announces it through the helper text's live region.
    if (Platform.OS !== 'web') {
      AccessibilityInfo.announceForAccessibility(ADDRESS_ERROR);
    }
  }, []);

  const onSelectPrediction = useCallback(
    (prediction: LocationPrediction) => {
      lookupIdRef.current += 1;
      const lookupId = lookupIdRef.current;

      setHasAddressError(false);
      onChangeRef.current(null);

      getPlaceDetails(prediction.placeId)
        .then(place => {
          if (lookupId !== lookupIdRef.current) {
            return;
          }

          // Cities, ZIPs and other areas resolve too, but have no street address to deliver to.
          if (!place?.address.addressLine1) {
            showAddressError();
            return;
          }

          onChangeRef.current({
            description: prediction.description,
            mainText: prediction.mainText,
            secondaryText: prediction.secondaryText,
            placeId: prediction.placeId,
            address: place.address,
            coordinates: place.coordinates,
          });
        })
        .catch(() => {
          if (lookupId === lookupIdRef.current) {
            showAddressError();
          }
        });
    },
    [getPlaceDetails, onChangeRef, showAddressError]
  );

  const onChangeText = useCallback(() => {
    lookupIdRef.current += 1;
    setHasAddressError(false);

    if (hasSelection) {
      onChangeRef.current(null);
    }
  }, [hasSelection, onChangeRef]);

  const onClear = useCallback(() => {
    lookupIdRef.current += 1;
    setHasAddressError(false);
    onChangeRef.current(null);
  }, [onChangeRef]);

  return (
    <AddressSearchInput
      ref={forwardedRef}
      value={valueDescription}
      placeholder={PLACEHOLDER}
      addressesOnly
      helperText={hasAddressError ? ADDRESS_ERROR : HELPER_TEXT}
      hasError={hasAddressError}
      disabled={disabled}
      returnKeyType="done"
      testID="store-locator-delivery-address-input"
      clearTestID="store-locator-delivery-address-clear"
      helperTestID="store-locator-delivery-address-helper"
      getPredictionTestID={getPredictionTestID}
      clearActionName="store locator delivery clear address"
      predictionActionName="store locator delivery select address"
      style={styles.container}
      onChangeText={onChangeText}
      onSelectPrediction={onSelectPrediction}
      onClear={onClear}
    />
  );
});

const useDeliveryAddressFieldMqStyles = createMqStyles({
  // The predictions overflow this block, so it has to stack above whatever follows it.
  container: {
    $base: {
      zIndex: 1,
    },
  },
});
